import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Route, Routes, useLocation } from "react-router";
import NotFound from "@/pages/NotFound";
import { routes } from "./routes";
import { AuthGuard } from "./AuthGuard";

/**
 * 与 dapp（wouter Switch + location）一致：用嵌套 `Routes location={location}`，
 * 离场时旧 motion 子树内仍匹配「上一 URL」，避免 `<Outlet />` 立刻换成新页导致动效错位。
 *
 * 外层路由需使用 `<Route path="/*" element={<AnimatedPageRoutes />} />` 包在 AppLayout 下。
 */
const NAV_ORDER = ["/", "/ai-trading", "/custody", "/team", "/profile"];

function getNavIndex(path: string): number {
  const idx = NAV_ORDER.indexOf(path);
  return idx >= 0 ? idx : -1;
}

const pageVariants = {
  initial: (direction: number) => ({
    x: direction > 0 ? "100%" : direction < 0 ? "-100%" : "8%",
    opacity: direction === 0 ? 0 : 1,
  }),
  animate: {
    x: "0%",
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? "-35%" : direction < 0 ? "35%" : "-8%",
    opacity: direction === 0 ? 0 : 0.6,
  }),
};

const pageTransition = {
  type: "tween" as const,
  duration: 0.25,
  ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
};

export function AnimatedPageRoutes() {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const direction = useMemo(() => {
    const prevIdx = getNavIndex(prevPathRef.current);
    const currIdx = getNavIndex(location.pathname);
    let dir = 0;
    if (prevIdx >= 0 && currIdx >= 0 && prevIdx !== currIdx) {
      dir = currIdx > prevIdx ? 1 : -1;
    }
    return dir;
  }, [location.pathname]);

  useEffect(() => {
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait" custom={direction} initial={false}>
      <motion.div
        key={location.pathname}
        custom={direction}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={pageTransition}
        className="overflow-x-hidden"
        style={{ minHeight: "100%", willChange: "transform, opacity" }}
      >
        <Routes location={location}>
          {routes.map(({ path, element: Page, meta }) => (
            <Route
              key={path}
              path={path}
              element={
                <AuthGuard meta={meta}>
                  <Page />
                </AuthGuard>
              }
            />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}
